
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { Column } from "@/types/array.types";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export const PAGE_SIZE = 15;
const supabase = useSupabaseClient();

export type SortOption = {
    key: string;
    label: string;
    by: "croissant" | "decroissant"
  };

export type TagType = {
    key: string,
    value: string,
    label: string
}

export type StatsType = {
    total_assets: number,
    total_value: number,
    last_update: string;
}

  
export const onExportData = async <T extends Record<string, any>>(columns: Column<T>[], columnsExport: string[], exportData: T[]) => { 
        
    const doc = new jsPDF();

    const body = exportData.map(row =>
      columns.map(column => {
        const value = row[column.key];
    
        if (column.key === "creation_date" || column.key === "date") {
          return value
            ? new Intl.DateTimeFormat("fr-FR", { dateStyle: "short" }).format(
                new Date(value)
              )
            : "";
        }
    
        return value ?? "";
      })
    );

    doc.text("Patrimoine", 15, 20);
    autoTable(doc, {
        head: [columnsExport],
        body
    });

    doc.save("rapport.pdf");
};

export const getRole = async () => {
    const { data, error } = await supabase.rpc("get_my_role")
    if (error) {
        console.error(error);
        return;

    }

    return { data }
};

export const getFilterData = async (table: string, column: string) => {
  const { data, error } = await supabase.rpc(
    "get_distinct_filter_values",
    {
      table_name: table,
      column_name: column,
    }
  );

  if (error) {
    console.error(error);
    return [];
  }

  return data.map((item) => item.value);
};

export const getAllData = async (table: string) => {
    const { data, error } = await supabase
    .from(table)
    .select("*")
  

      if (error) {
        console.error(error);
        return;
      }

    return { data, error }
}

export const getData = async (
    page: number,
    table: string,
    sortOption?: SortOption,
    tags?: TagType[]
  ) => {
    const from = (page - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let query = supabase
      .from(table)
      .select("*, documents ( storage_path )", 
        { count: "exact" });
  
    if (tags && tags.length > 0) {  
        tags.forEach((tag) => {
          if(tag.value == "Oui"){
            query = query.eq(tag.key, true);
          }
          else if (tag.value == "Non") {
            query = query.eq(tag.key, false);
          }
          else {
            query = query.eq(tag.key, tag.value);
          }
          });
    }
    if (sortOption) {
        query = query.order(sortOption.key, {
          ascending: sortOption.by === "croissant",
        });
      }
    
      const { data, count, error } = await query.range(from, to);

      if (error) {
        console.error(error);
        return;
      }

      const dataWithImages = await Promise.all(
        data.map(async (item) => {
          if (!item.documents?.storage_path) {
            return item;
          }

          const { data: signedUrl } = await supabase.storage
            .from("documents")
            .createSignedUrl(item.documents.storage_path, 3600, {transform: {
              width: 120,
              height: 120,
              resize: "cover",
              quality: 50,
        }});

          return {
            ...item,
            imageUrl: signedUrl?.signedUrl,
          };
        })
      );

      return { 
        data: dataWithImages, 
        count, 
        error 
      };
    };

export const getStats = async (view: string) => {
    const { data, error } = await supabase
    .from(view)
    .select()
    .single();

    if (error) {
        console.error(error);
        return;

    }
    return { data, error }
}
  

export const onDelete = async (id: string, table: string) => {
    const { error } = await supabase.from(table).delete().eq("id", id);
    if (error) {
      console.error(error);
      return;
    }  
};

export const onCreate = async <T extends Record<string, any>>(id: string, values: T, table: string) => {
    const data = {...values, created_at: new Date(), created_by: id};
    const { error } = await supabase.from(table).insert(data);
    if (error) {
        console.error(error);
        return;
    }    
};

export const onUpdate = async <T extends Record<string, any>>(user_id: string, values: T, table: string, id: string) => {
    const data = {...values, updated_by: user_id};
  
    const { error } = await supabase.from(table).update(data).match({id});
    if (error) {
        console.error(error);
        return;
    }
};

export const onUploadPhoto = async (photo: File) => {
  const fileName = `${crypto.randomUUID()}-${photo.name}`;
 
  const { data: file, error } = await supabase.storage
    .from("documents")
    .upload(fileName, photo);

    if (error) {
      console.error(error);
      return;
    }

  const { data: document } = await supabase
    .from("documents")
    .insert({
      storage_path: file.path,
      file_name: photo.name,
    })
    .select()
    .single();

  return { document } ;
};

export const getPreview = async (photoId: string) => {
  
    const { data, error } = await supabase
    .from("art")
    .select(`
      *,
      documents (
        storage_path
      )
    `)
    .eq("id", photoId)
    .single();

    const { data: signedUrl } = await supabase.storage
    .from("documents")
    .createSignedUrl(
      data.documents.storage_path,
      3600
    );

    return { signedUrl, error } ;
};